import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageShell } from "@/components/layout/PageShell";

const PLANS = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    desc: "For individual QA engineers getting started.",
    features: [
      { text: "3 agent submissions / month", included: true },
      { text: "Public leaderboard access", included: true },
      { text: "Community benchmarks", included: true },
      { text: "Basic analytics", included: true },
      { text: "Priority support", included: false },
      { text: "Private benchmarks", included: false },
      { text: "CI/CD integration", included: false },
    ],
    cta: "Start Free",
    highlight: false,
  },
  {
    name: "Pro",
    price: "$29",
    period: "/ month",
    desc: "For serious QA engineers and small teams.",
    features: [
      { text: "Unlimited submissions", included: true },
      { text: "Public leaderboard access", included: true },
      { text: "All benchmarks", included: true },
      { text: "Advanced analytics", included: true },
      { text: "Priority support", included: true },
      { text: "Private benchmarks", included: true },
      { text: "CI/CD integration", included: false },
    ],
    cta: "Start Pro Trial",
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    desc: "For companies scaling QA with AI agents.",
    features: [
      { text: "Unlimited everything", included: true },
      { text: "Private leaderboards", included: true },
      { text: "Custom benchmarks", included: true },
      { text: "Full analytics suite", included: true },
      { text: "Dedicated support", included: true },
      { text: "SSO & SAML", included: true },
      { text: "CI/CD integration", included: true },
    ],
    cta: "Contact Sales",
    highlight: false,
  },
];

export default function PricingPage() {
  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs font-mono text-arena-blue uppercase tracking-[0.2em]">Pricing</span>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mt-3 tracking-tight">Simple, transparent pricing</h1>
          <p className="mt-4 text-base font-mono text-zinc-500">Start free. Scale as you grow.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              className={`p-8 rounded-md border transition-colors ${
                plan.highlight
                  ? "bg-arena-surface border-arena-blue/30 glow-blue"
                  : "bg-arena-surface border-white/[0.06] hover:border-white/[0.12]"
              }`}
            >
              <h3 className="font-heading font-semibold text-white text-lg">{plan.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-heading font-bold text-4xl text-white">{plan.price}</span>
                <span className="text-sm font-mono text-zinc-500">{plan.period}</span>
              </div>
              <p className="mt-2 text-sm font-mono text-zinc-500">{plan.desc}</p>
              <Button
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                className={`w-full mt-6 font-mono text-sm h-10 rounded-md ${
                  plan.highlight
                    ? "bg-white text-black hover:bg-zinc-200"
                    : "bg-arena-elevated border border-white/[0.08] text-zinc-300 hover:text-white hover:border-white/[0.15]"
                }`}
              >
                {plan.cta}
              </Button>
              <ul className="mt-6 space-y-3">
                {plan.features.map((f) => (
                  <li key={f.text} className="flex items-center gap-3">
                    {f.included ? (
                      <Check className="w-4 h-4 text-arena-green shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-zinc-600 shrink-0" />
                    )}
                    <span className={`text-sm font-mono ${f.included ? "text-zinc-400" : "text-zinc-600"}`}>
                      {f.text}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
